import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  applyGroupMemberRole,
  getUsersByGroupQueryKey,
  useAddMembersToGroupMutation,
  useChangeGroupUserRoleMutation,
  useCreateNewGroupMutation,
  useGetUsersByGroupOptions,
  useRemoveMembersFromGroupMutation,
} from '@/hooks/queries/groups';
import { getQueryClient } from '@/integrations/tanstack-query/RootProvider';
import { Users, X, PlusIcon, Loader } from 'lucide-react';
import { memo, useCallback, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LegacyGroupUser } from '@/api/services/groups';
import { cn } from '@/lib/utils';
import {
  getGroupRoleBadgeVariant,
  getGroupRoleLabel,
  GROUP_USER_ROLE_OPTIONS,
  resolveGroupUserRole,
} from '@/lib/groupRoles';
import { toast } from 'sonner';
import type { UserDetails } from '@/types';
import { DialogTrigger } from '@radix-ui/react-dialog';
import type { GroupDetails, GroupUserRole } from '@/types/groups';
import InviteUserByEmail from './InviteUserByEmail';

type UsersToAdd = {
  id: number;
  role: GroupUserRole;
  user: UserDetails;
};

type DisplayMember =
  | UsersToAdd
  | {
    id: number | string;
    role: GroupUserRole;
    user: UserDetails;
    isPendingInvite?: false;
  }
  | {
    id: string;
    role: GroupUserRole;
    user: UserDetails;
    isPendingInvite: true;
  };

/**
 * Reusable dialog component for displaying group details and managing users
 */
export function CreateNewGroupDialog({
  compact = false,
  trigger,
  onGroupCreated,
}: CreateNewGroupDialogProps) {
  const groupNameRef = useRef<HTMLInputElement>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<GroupUserRole>('READER');

  const {
    groupId,
    setGroupName,
    groupCreationStatus,
    reset,
    usersToAdd,
    setUsersToAdd,
    handleCreateAndAddMembersToGroup,
    handleAddNewUserToList,
    handleRemoveUserFromList,
    handleChangeMemberRole,
    handleGroupNameBlur,
    groupName,
    displayedMembers,
    isMembersLoading,
  } = useCreateNewGroup(groupNameRef, () => handleOpenChange(false), onGroupCreated, inviteRole);

  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback((open: boolean) => {
    setOpen(open);
    if (!open) {
      setInviteEmail('');
      setInviteRole('READER');
      setUsersToAdd([]);
      if (groupNameRef.current) {
        groupNameRef.current.value = '';
        reset();
      }
    }
  }, [groupNameRef, reset, setUsersToAdd]);

  const triggerElement = trigger || (
    <Button variant={compact ? 'ghost' : 'outline'} className='h-8 w-8' size={compact ? 'icon' : 'default'}>
      <PlusIcon />
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerElement}
      </DialogTrigger>
      <DialogContent className="max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span className="text-lg font-medium">Create New Group</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleOpenChange(false)}
              type="button"
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Group Info Section */}
          <div className="space-y-2">
            <h3 className="text-muted-foreground text-sm font-medium">Group Information</h3>
            <div className="space-y-1 py-1">
              <p className="text-sm">
                <strong>Name*</strong>
              </p>
              <Input
                type="text"
                inputClassName="p-2 px-4"
                placeholder="Enter group name"
                onChange={(e) => setGroupName((e.target as HTMLInputElement)?.value || '')}
                onBlur={() => {
                  void handleGroupNameBlur();
                }}
                ref={groupNameRef}
                required
                autoFocus
                disabled={groupCreationStatus === 'creating'}
              />
              {!groupId ? (
                <p className="text-muted-foreground text-xs">
                  Leave this field to create the group, then add members below.
                </p>
              ) : (
                <p className="text-xs text-green-700">Group created — you can add members now.</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Members Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-muted-foreground text-sm font-medium">
                Members {displayedMembers.length > 0 && `(${displayedMembers.length})`}
              </h3>
            </div>

            {/* Invite User Section */}
            <InviteUserByEmail
              value={inviteEmail}
              onValueChange={setInviteEmail}
              groupId={groupId}
              onKnownUser={handleAddNewUserToList}
              inviteRole={inviteRole}
              onInviteRoleChange={setInviteRole}
              canAddMembersToGroup={Boolean(groupId)}
              buttonClassName="bg-primary/10 shrink-0"
              disabled={!groupId || groupCreationStatus === 'creating'}
            />

            {!groupId ? null : groupId && isMembersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="h-5 w-5 animate-spin" />
              </div>
            ) : displayedMembers.length > 0 ? (
              <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg py-2">
                {displayedMembers.map((member) => (
                  <CreateNewGroupDialog.MemberItem
                    key={String(member.id)}
                    id={typeof member.id === 'number' ? member.id : undefined}
                    role={member.role}
                    selectedUser={member.user}
                    isPendingInvite={'isPendingInvite' in member && member.isPendingInvite}
                    canChangeRole={Boolean(groupId && !('isPendingInvite' in member && member.isPendingInvite))}
                    isLocalOnly={!groupId}
                    handleRemoveUserFromList={handleRemoveUserFromList}
                    onRoleChange={handleChangeMemberRole}
                  />
                ))}
              </div>
            ) : (
              <div className="text-muted-foreground py-8 text-center">
                <Users className="mx-auto mb-2 h-12 w-12 opacity-50" />
                <p>No members found. Add members to the group</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant={'default'}
            onClick={handleCreateAndAddMembersToGroup}
            disabled={groupCreationStatus === 'creating' || !groupName?.trim()}
          >
            {groupCreationStatus === 'creating' ? (
              <>
                <Loader className="mr-1 h-4 w-4" />
                Creating...
              </>
            ) : groupId ? (
              <>Done</>
            ) : (
              <>Create Group</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Member Item Component
CreateNewGroupDialog.MemberItem = memo(
  ({
    role,
    id,
    selectedUser,
    isPendingInvite = false,
    canChangeRole = false,
    isLocalOnly = false,
    handleRemoveUserFromList,
    onRoleChange,
  }: {
    role: GroupUserRole;
    id?: number;
    selectedUser: UserDetails;
    isPendingInvite?: boolean;
    canChangeRole?: boolean;
    isLocalOnly?: boolean;
    handleRemoveUserFromList: (userId: number) => void;
    onRoleChange: (userId: number, role: GroupUserRole, isLocalOnly: boolean) => void;
  }) => {
    return (
      <div className="flex items-center justify-between rounded-md border p-2">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
            <span
              className={cn(
                'text-xs font-medium',
                (!selectedUser?.isEnabled || selectedUser?.isBlocked) && 'line-through'
              )}
            >
              {selectedUser?.firstName?.charAt(0).toUpperCase() ||
                selectedUser?.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium">{selectedUser?.firstName || 'Unknown'}</p>
            <p className="text-muted-foreground text-xs">{selectedUser?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPendingInvite ? (
            <Badge variant="outline">Invited</Badge>
          ) : canChangeRole && id != null ? (
            <Select
              value={role}
              onValueChange={(nextRole) =>
                onRoleChange(id, nextRole as GroupUserRole, isLocalOnly)
              }
            >
              <SelectTrigger size="sm" className="w-[110px]" aria-label="Member role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_USER_ROLE_OPTIONS.map(({ value: roleValue, label }) => (
                  <SelectItem key={roleValue} value={roleValue}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant={getGroupRoleBadgeVariant(role)}>
              {getGroupRoleLabel(role)}
            </Badge>
          )}
          {!isPendingInvite && id != null ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleRemoveUserFromList(id)}
            >
              <X className="h-5 w-5" />
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
);

const useCreateNewGroup = (
  groupNameRef: React.RefObject<HTMLInputElement | null>,
  handleClose?: () => void,
  onGroupCreated?: (group: GroupDetails) => void | Promise<void>,
  inviteRole: GroupUserRole = 'READER',
) => {
  const [groupId, setGroupId] = useState<string>('');
  const [groupName, setGroupName] = useState<string>('');
  const [usersToAdd, setUsersToAdd] = useState<UsersToAdd[]>([]);
  const [groupCreationStatus, setGroupCreationStatus] = useState<'idle' | 'creating' | 'success' | 'error'>('idle');

  const { queryClient } = getQueryClient();
  const { mutateAsync: createNewGroup, isPending: isCreatingGroup } = useCreateNewGroupMutation();
  const { mutateAsync: addMembersToGroup } = useAddMembersToGroupMutation();
  const { mutateAsync: removeMembersFromGroup } = useRemoveMembersFromGroupMutation();
  const { mutateAsync: changeGroupUserRole } = useChangeGroupUserRoleMutation();

  const { data: groupMembersData, isLoading: isMembersLoading } = useQuery(
    useGetUsersByGroupOptions(groupId, Boolean(groupId), { includePending: true }),
  );

  const displayedMembers = useMemo((): DisplayMember[] => {
    if (!groupId) {
      return usersToAdd;
    }

    const legacyData = groupMembersData?.data as LegacyGroupUser | undefined;
    const members =
      legacyData?.members?.map((member) => ({
        id: member.user.id,
        role: resolveGroupUserRole(member),
        user: member.user,
      })) ?? [];

    const pendingInvites =
      legacyData?.pendingInvites?.map((invite) => ({
        id: `pending-${invite.inviteeUserEmail}`,
        role: 'READER' as const,
        isPendingInvite: true as const,
        user: {
          id: 0,
          email: invite.inviteeUserEmail,
          firstName: 'Invited',
          lastName: 'User',
        } as UserDetails,
      })) ?? [];

    return [...members, ...pendingInvites];
  }, [groupId, groupMembersData?.data, usersToAdd]);

  const handleAddNewUserToList = useCallback(
    async (user: UserDetails) => {
      const roleToApply = inviteRole;

      if (!groupId) {
        setUsersToAdd((prev) => {
          if (prev.some((entry) => entry.id === user.id)) {
            toast.error('User already added');
            return prev;
          }
          return [...prev, { id: user.id, role: roleToApply, user }];
        });
        return;
      }

      const isAlreadyMember = displayedMembers.some(
        (member) =>
          !('isPendingInvite' in member && member.isPendingInvite) &&
          String(member.user.id) === String(user.id),
      );
      if (isAlreadyMember) {
        toast.error('User already added');
        return;
      }

      try {
        await addMembersToGroup({ groupId, userIds: [user.id] });
        await applyGroupMemberRole(groupId, user.id, roleToApply);
        await queryClient.invalidateQueries({
          queryKey: getUsersByGroupQueryKey(groupId),
        });
      } catch (error: unknown) {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : `Failed to add user ${user.email}`,
        );
      }
    },
    [addMembersToGroup, displayedMembers, groupId, inviteRole, queryClient],
  );

  const handleRemoveUserFromList = useCallback(
    async (userId: number) => {
      if (!groupId) {
        setUsersToAdd((prev) => prev.filter((user) => user.id !== userId));
        return;
      }

      try {
        await removeMembersFromGroup({ groupId, userIds: [userId] });
      } catch {
        // Mutation shows toast on error.
      }
    },
    [groupId, removeMembersFromGroup],
  );

  const handleChangeMemberRole = useCallback(
    async (userId: number, role: GroupUserRole, isLocalOnly: boolean) => {
      if (isLocalOnly || !groupId) {
        setUsersToAdd((prev) =>
          prev.map((user) => (user.id === userId ? { ...user, role } : user)),
        );
        return;
      }

      try {
        await changeGroupUserRole({ groupId, userId, role });
      } catch {
        // Mutation shows toast on error.
      }
    },
    [changeGroupUserRole, groupId],
  );

  const reset = useCallback(() => {
    setGroupId('');
    setGroupName('');
    setUsersToAdd([]);
    setGroupCreationStatus('idle');
  }, []);

  const handleAddMembersToGroup = useCallback(
    async (targetGroupId: string, membersList: UsersToAdd[] = usersToAdd) => {
      if (membersList.length === 0) return null;

      const userIds = membersList.map((user) => user.id);
      const result = await addMembersToGroup({ groupId: targetGroupId, userIds });

      await Promise.all(
        membersList
          .filter((member) => member.role !== 'READER')
          .map((member) => applyGroupMemberRole(targetGroupId, member.id, member.role)),
      );

      await queryClient.invalidateQueries({
        queryKey: getUsersByGroupQueryKey(targetGroupId),
      });

      return result;
    },
    [addMembersToGroup, queryClient, usersToAdd],
  );

  const handleGroupCreatedCallback = useCallback(
    async (createdGroup?: GroupDetails) => {
      if (!createdGroup || !onGroupCreated) return;
      try {
        await onGroupCreated(createdGroup);
      } catch (error) {
        toast.error((error as Error)?.message || 'Group created but failed to place it');
      }
    },
    [onGroupCreated],
  );

  const handleEnsureGroupCreated = useCallback(async () => {
    const trimmedName = groupName.trim();
    if (!trimmedName) {
      return null;
    }
    if (groupId) {
      return { status: true, data: { id: groupId } };
    }
    if (isCreatingGroup || groupCreationStatus === 'creating') {
      return null;
    }

    setGroupCreationStatus('creating');
    try {
      const group = await createNewGroup(trimmedName);
      if (!group?.status) {
        setGroupCreationStatus('error');
        return null;
      }

      const newGroupId = String(group.data.id);
      setGroupId(newGroupId);
      await handleGroupCreatedCallback(group.data);
      await queryClient.invalidateQueries({
        queryKey: getUsersByGroupQueryKey(newGroupId),
      });

      if (usersToAdd.length > 0) {
        await handleAddMembersToGroup(newGroupId, usersToAdd).catch((error: unknown) => {
          toast.error(
            error instanceof Error && error.message
              ? error.message
              : 'Group created but failed to add members',
          );
        });
      }

      setGroupCreationStatus('success');
      return group;
    } catch (error: unknown) {
      setGroupCreationStatus('error');
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to create group',
      );
      return null;
    }
  }, [
    createNewGroup,
    groupCreationStatus,
    groupId,
    groupName,
    handleAddMembersToGroup,
    handleGroupCreatedCallback,
    isCreatingGroup,
    queryClient,
    usersToAdd,
  ]);

  const handleGroupNameBlur = useCallback(async () => {
    await handleEnsureGroupCreated();
  }, [handleEnsureGroupCreated]);

  const handleCreateAndAddMembersToGroup = useCallback(async () => {
    if (groupId) {
      handleClose?.();
      reset();
      return;
    }

    const group = await handleEnsureGroupCreated();
    if (group?.status) {
      handleClose?.();
      reset();
    }
  }, [groupId, handleClose, handleEnsureGroupCreated, reset]);

  return {
    groupId,
    groupName,
    setGroupName,
    createNewGroup,
    isCreatingGroup,
    groupCreationStatus,
    reset,
    usersToAdd,
    setUsersToAdd,
    handleAddNewUserToList,
    handleRemoveUserFromList,
    handleChangeMemberRole,
    handleCreateAndAddMembersToGroup,
    handleAddMembersToGroup,
    handleEnsureGroupCreated,
    handleGroupNameBlur,
    displayedMembers,
    isMembersLoading,
  };
};

type CreateNewGroupDialogProps = {
  compact?: boolean;
  trigger?: ReactElement;
  onGroupCreated?: (group: GroupDetails) => void | Promise<void>;
};
